/**
 * WorkerCabinet — 4-tab worker cabinet
 * Tabs: Мой день | Мои заказы | Мои часы | Мои выплаты
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { OrderPhotosGallery } from '@/components/PhotosFiles';
import {
  Calendar,
  Briefcase,
  Clock,
  CheckCircle2,
  AlertCircle,
  Play,
  Square,
  Phone,
  MessageSquare,
  MapPin,
  Navigation,
  Loader2,
  AlertTriangle,
  Send,
  Wrench,
  DollarSign,
  ChevronRight,
  Car,
  Coffee,
  Shield,
  TrendingUp,
  FileText,
  Camera,
} from 'lucide-react';
import { cn, localToday, localDaysAgo } from '@/lib/utils';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  new: { label: 'Новый', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  confirmed: { label: 'Подтверждён', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  in_progress: { label: 'В работе', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  done: { label: 'Выполнен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  cancelled: { label: 'Отменён', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const GPS_STATUS_CONFIG = {
  verified: { label: 'GPS подтверждён', color: 'text-green-600', icon: Shield, bg: 'bg-green-50 dark:bg-green-900/10' },
  warning: { label: 'GPS ~300м', color: 'text-amber-600', icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-900/10' },
  flagged: { label: 'GPS далеко', color: 'text-red-600', icon: AlertCircle, bg: 'bg-red-50 dark:bg-red-900/10' },
  unavailable: { label: 'GPS нет', color: 'text-gray-400', icon: MapPin, bg: 'bg-gray-50 dark:bg-gray-900/10' },
};

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const rlat1 = (lat1 * Math.PI) / 180;
  const rlat2 = (lat2 * Math.PI) / 180;
  const dlat = ((lat2 - lat1) * Math.PI) / 180;
  const dlng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dlat / 2) ** 2 + Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(dlng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatMinutes(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h > 0 && m > 0) return `${h}ч ${m}мин`;
  if (h > 0) return `${h}ч`;
  return `${m}мин`;
}

function formatDayTime(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTime(totalSeconds) {
  const abs = Math.abs(totalSeconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const sign = totalSeconds < 0 ? '-' : '';
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${sign}${m}:${String(s).padStart(2, '0')}`;
}

function formatDuration(minutes) {
  if (!minutes) return '?';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h} ч ${m} мин`;
  if (h > 0) return `${h} ч`;
  return `${m} мин`;
}

// ─── TAB 1: МОЙ ДЕНЬ ─────────────────────────────────────────────

function MyDayTab({ dayStatus, orders, onDayAction, onOrderAction, actionLoading, api, fetchData }) {
  const [dayElapsed, setDayElapsed] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [pressure, setPressure] = useState(null);
  const [showTimeRequest, setShowTimeRequest] = useState(false);
  const [reqHours, setReqHours] = useState(0);
  const [reqMinutes, setReqMinutes] = useState(15);
  const [reqReason, setReqReason] = useState('');
  const [reqLoading, setReqLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [endDayConfirm, setEndDayConfirm] = useState(false);

  const isHourly = dayStatus?.payment_type !== 'percentage';
  const hasActiveOrders = orders.some(o => ['new', 'confirmed', 'in_progress'].includes(o.operational_status));
  const allDone = orders.length === 0 || orders.every(o => o.operational_status === 'done' || o.operational_status === 'cancelled');

  const handleEndDayClick = () => {
    if (hasActiveOrders) {
      setEndDayConfirm(true); // show warning
    } else {
      onDayAction('end');
    }
  };

  // Day timer
  useEffect(() => {
    if (!dayStatus?.is_day_started || !dayStatus?.day_start_time) {
      setDayElapsed(0);
      return;
    }
    const startTime = new Date(dayStatus.day_start_time + (dayStatus.day_start_time.includes('Z') ? '' : 'Z'));
    const tick = () => {
      const endTime = dayStatus.day_end_time
        ? new Date(dayStatus.day_end_time + (dayStatus.day_end_time.includes('Z') ? '' : 'Z'))
        : new Date();
      setDayElapsed(Math.max(0, Math.floor((endTime - startTime) / 1000)));
    };
    tick();
    if (!dayStatus.day_end_time) {
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }
  }, [dayStatus?.is_day_started, dayStatus?.day_start_time, dayStatus?.day_end_time]);

  // Fetch my-day breakdown + schedule pressure
  useEffect(() => {
    const today = localToday();
    api.get(`/worker/my-day/${today}`).then(res => {
      setBreakdown(res.data?.breakdown);
      setPressure(res.data?.schedule_pressure);
    }).catch(() => {});
    api.get('/time-adjustments/my-pending').then(res => {
      // API now returns a list of requests for today
      const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      setPendingRequest(list);
    }).catch(() => {});
  }, [api]);

  const handleSubmitTimeRequest = async () => {
    const total = reqHours * 60 + reqMinutes;
    if (total <= 0) { toast.error('Выберите время'); return; }
    if (reqReason.trim().length < 10) { toast.warning('Опишите причину подробнее (мин. 10 символов)'); return; }
    setReqLoading(true);
    try {
      const today = localToday();
      await api.post('/time-adjustments', {
        date: today,
        requested_hours: reqHours,
        requested_minutes: reqMinutes,
        reason: reqReason.trim(),
      });
      toast.success('Запрос отправлен менеджеру');
      setShowTimeRequest(false);
      const res = await api.get('/time-adjustments/my-pending');
      const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      setPendingRequest(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setReqLoading(false);
    }
  };

  const handleOrderActionWrapped = async (action, orderId, noteText) => {
    await onOrderAction(action, orderId, noteText);
    if (action === 'finish-work') setDetailOpen(false);
  };

  const activeOrders = orders.filter(o => o.operational_status === 'in_progress');
  const pendingOrders = orders.filter(o => ['new', 'confirmed'].includes(o.operational_status));
  const doneOrders = orders.filter(o => o.operational_status === 'done');

  const today = new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold capitalize">{today}</h2>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="cgc-card">
          <CardContent className="p-2.5 text-center">
            <p className="text-xl font-bold tabular-nums text-accent">{dayStatus?.total_orders_today || 0}</p>
            <p className="text-[10px] text-muted-foreground">Заказов</p>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-2.5 text-center">
            <p className="text-xl font-bold tabular-nums text-amber-500">{dayStatus?.in_progress_orders_today || 0}</p>
            <p className="text-[10px] text-muted-foreground">В работе</p>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-2.5 text-center">
            <p className="text-xl font-bold tabular-nums text-green-500">{dayStatus?.completed_orders_today || 0}</p>
            <p className="text-[10px] text-muted-foreground">Выполнено</p>
          </CardContent>
        </Card>
      </div>

      {/* Day timer / start/end buttons */}
      {isHourly && !dayStatus?.is_day_started && !dayStatus?.day_end_time && (
        hasActiveOrders ? (
          <Button
            className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
            onClick={() => onDayAction('start')}
            disabled={actionLoading}
          >
            {actionLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />}
            Начать рабочий день
          </Button>
        ) : (
          <div className="p-3 rounded-xl bg-muted/50 border text-center">
            <p className="text-sm text-muted-foreground">Нет заказов на сегодня</p>
            <p className="text-xs text-muted-foreground mt-1">Рабочий день можно начать только при наличии активных заказов</p>
          </div>
        )
      )}

      {dayStatus?.is_day_started && !dayStatus?.day_end_time && (
        <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">Рабочий день</span>
            </div>
            <span className="text-xl font-mono font-bold tabular-nums text-green-600 dark:text-green-400">
              {formatDayTime(dayElapsed)}
            </span>
          </div>
          <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 flex items-center justify-between">
            <span className="text-xs text-green-600/70">
              {allDone ? 'Все заказы выполнены' : `Осталось заказов: ${orders.filter(o => ['new','confirmed','in_progress'].includes(o.operational_status)).length}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
              onClick={handleEndDayClick}
              disabled={actionLoading}
            >
              <Square className="mr-1.5 h-3 w-3" /> Завершить день
            </Button>
          </div>
        </div>
      )}

      {/* Confirm end day with pending orders */}
      {endDayConfirm && (
        <div className="p-4 rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/10 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">Завершить день?</p>
              <p className="text-xs text-amber-700 dark:text-amber-500 mt-1">
                У вас ещё есть незавершённые заказы ({orders.filter(o => ['new','confirmed','in_progress'].includes(o.operational_status)).length} шт.). Вы уверены что хотите закончить рабочий день?
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setEndDayConfirm(false)}
            >
              Отмена
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { setEndDayConfirm(false); onDayAction('end'); }}
              disabled={actionLoading}
            >
              <Square className="mr-1.5 h-3 w-3" /> Да, завершить
            </Button>
          </div>
        </div>
      )}

      {/* Schedule Pressure alert */}
      {pressure && pressure.status !== 'OK' && (
        <div className={cn(
          "p-3 rounded-xl border flex items-start gap-2",
          pressure.status === 'LATE' && "bg-red-50 dark:bg-red-900/10 border-red-300",
          pressure.status === 'TIGHT' && "bg-amber-50 dark:bg-amber-900/10 border-amber-300",
          pressure.status === 'WATCH' && "bg-blue-50 dark:bg-blue-900/10 border-blue-300",
        )}>
          <AlertTriangle className={cn(
            "h-4 w-4 shrink-0 mt-0.5",
            pressure.status === 'LATE' && "text-red-600",
            pressure.status === 'TIGHT' && "text-amber-600",
            pressure.status === 'WATCH' && "text-blue-600",
          )} />
          <div>
            <p className="text-sm font-medium">{pressure.message}</p>
            {pressure.travel_minutes > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                <Car className="h-3 w-3 inline mr-1" />
                Дорога: ~{pressure.travel_minutes} мин
              </p>
            )}
          </div>
        </div>
      )}

      {/* Fair Hours Timeline (if breakdown available) */}
      {breakdown && breakdown.blocks && breakdown.blocks.length > 0 && (
        <Card className="cgc-card">
          <CardContent className="p-3">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Clock className="h-4 w-4" /> Таймлайн дня
            </h3>
            <div className="space-y-1.5">
              {breakdown.blocks.map((block, idx) => (
                <TimelineBlock key={idx} block={block} />
              ))}
            </div>
            <div className="mt-3 pt-2 border-t grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Работа</p>
                <p className="text-sm font-semibold text-green-600">{formatMinutes(breakdown.total_work_minutes)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Дорога</p>
                <p className="text-sm font-semibold text-blue-600">{formatMinutes(breakdown.total_travel_minutes)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Перерыв</p>
                <p className="text-sm font-semibold text-gray-500">{formatMinutes(breakdown.total_break_minutes)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active orders */}
      {activeOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
            <Play className="h-4 w-4" /> В работе
          </h3>
          {activeOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} isActive />
          ))}
        </div>
      )}

      {/* Pending orders */}
      {pendingOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Calendar className="h-4 w-4" /> Предстоящие ({pendingOrders.length})
          </h3>
          {pendingOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} />
          ))}
        </div>
      )}

      {/* Done orders */}
      {doneOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Выполнено ({doneOrders.length})
          </h3>
          {doneOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} />
          ))}
        </div>
      )}

      {orders.length === 0 && (
        <Card className="cgc-card">
          <CardContent className="p-8 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">Нет заказов на сегодня</p>
          </CardContent>
        </Card>
      )}

      {/* Day ended — summary + time request */}
      {dayStatus?.day_end_time && isHourly && (
        <DayEndedSection
          dayElapsed={dayElapsed}
          pendingRequest={pendingRequest}
          showTimeRequest={showTimeRequest}
          setShowTimeRequest={setShowTimeRequest}
          reqHours={reqHours} setReqHours={setReqHours}
          reqMinutes={reqMinutes} setReqMinutes={setReqMinutes}
          reqReason={reqReason} setReqReason={setReqReason}
          reqLoading={reqLoading}
          onSubmit={handleSubmitTimeRequest}
        />
      )}

      {/* Order detail sheet */}
      <OrderDetailSheet
        order={selectedOrder}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onAction={handleOrderActionWrapped}
        actionLoading={actionLoading}
        allOrders={orders}
      />
    </div>
  );
}

// ─── Timeline Block ───────────────────────────────────────────────

function TimelineBlock({ block }) {
  const startTime = block.start_time ? new Date(block.start_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
  const endTime = block.end_time ? new Date(block.end_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';

  if (block.type === 'work') {
    const gpsConf = GPS_STATUS_CONFIG[block.gps_start_status] || GPS_STATUS_CONFIG.unavailable;
    const GpsIcon = gpsConf.icon;
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200/50">
        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
          <Briefcase className="h-4 w-4 text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{block.order_title || 'Работа'}</p>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{startTime} — {endTime}</span>
            <span className="font-medium">{formatMinutes(block.duration_minutes)}</span>
            {block.gps_start_status && (
              <span className={cn("flex items-center gap-0.5", gpsConf.color)}>
                <GpsIcon className="h-3 w-3" />
              </span>
            )}
          </div>
          {block.speed_diff_minutes != null && block.speed_diff_minutes > 0 && (
            <p className="text-[10px] text-green-600 font-medium mt-0.5">
              <TrendingUp className="h-3 w-3 inline mr-0.5" />
              На {block.speed_diff_minutes} мин быстрее!
            </p>
          )}
        </div>
        <span className="text-xs font-medium text-green-600 shrink-0">
          {block.is_paid ? 'оплач.' : ''}
        </span>
      </div>
    );
  }

  if (block.type === 'travel') {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200/50">
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
          <Car className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Дорога</p>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{startTime} — {endTime}</span>
            <span className="font-medium">{formatMinutes(block.duration_minutes)}</span>
            {block.osrm_distance_km && (
              <span>{block.osrm_distance_km} км</span>
            )}
          </div>
        </div>
        <span className={cn("text-xs font-medium shrink-0", block.is_paid ? "text-blue-600" : "text-gray-400")}>
          {block.is_paid ? 'оплач.' : 'нет'}
        </span>
      </div>
    );
  }

  // break
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/10 border border-gray-200/50">
      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
        <Coffee className="h-4 w-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-600">Перерыв</p>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{startTime} — {endTime}</span>
          <span className="font-medium">{formatMinutes(block.duration_minutes)}</span>
        </div>
      </div>
      <span className="text-xs font-medium text-gray-400 shrink-0">не оплач.</span>
    </div>
  );
}

// ─── Day Ended Section ────────────────────────────────────────────

function DayEndedSection({ dayElapsed, pendingRequest, showTimeRequest, setShowTimeRequest, reqHours, setReqHours, reqMinutes, setReqMinutes, reqReason, setReqReason, reqLoading, onSubmit }) {
  // pendingRequest is now a list of requests for today
  const requests = Array.isArray(pendingRequest) ? pendingRequest : (pendingRequest ? [pendingRequest] : []);
  const latestReq = requests.length > 0 ? requests[requests.length - 1] : null;
  const initialReq = requests.length > 0 ? requests[0] : null;
  // Can appeal: only if there's exactly 1 request and it was rejected
  const canAppeal = requests.length === 1 && initialReq?.status === 'rejected';
  // Can submit first request: no requests exist yet
  const canSubmitFirst = requests.length === 0;

  return (
    <div className="space-y-2">
      <div className="p-3 rounded-xl bg-muted/50 border text-center">
        <p className="text-sm text-muted-foreground">День завершён</p>
        <p className="text-lg font-mono font-bold tabular-nums mt-1">{formatDayTime(dayElapsed)}</p>
      </div>

      {/* Show all requests history */}
      {requests.map((req, i) => (
        <div key={req.id || i}>
          {req.status === 'pending' && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                {req.is_appeal ? 'Апелляция отправлена' : 'Запрос на доп. время отправлен'}
              </p>
              <p className="text-xs text-amber-600/70 mt-1">
                +{req.requested_hours}ч {req.requested_minutes > 0 ? `${req.requested_minutes}мин` : ''} — ожидает решения
              </p>
            </div>
          )}

          {req.status === 'approved' && (
            <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                {req.is_appeal ? 'Апелляция одобрена' : 'Доп. время одобрено'}
              </p>
              <p className="text-xs text-green-600/70 mt-1">
                +{req.approved_hours}ч {req.approved_minutes > 0 ? `${req.approved_minutes}мин` : ''}
              </p>
              {req.manager_comment && (
                <p className="text-xs text-green-600/60 mt-1 italic">«{req.manager_comment}»</p>
              )}
            </div>
          )}

          {req.status === 'rejected' && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">
                {req.is_appeal ? 'Апелляция отклонена' : 'Запрос отклонён'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                +{req.requested_hours}ч {req.requested_minutes > 0 ? `${req.requested_minutes}мин` : ''}
                {' '}· {req.reason}
              </p>
              {req.manager_comment && (
                <p className="text-xs text-red-600/70 mt-1 italic">Менеджер: «{req.manager_comment}»</p>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Appeal button: exactly 1 rejected request → allow one more */}
      {canAppeal && (
        <>
          {!showTimeRequest ? (
            <Button variant="outline" className="w-full border-amber-300 text-amber-700" onClick={() => setShowTimeRequest(true)}>
              <AlertTriangle className="mr-2 h-4 w-4" /> Подать апелляцию
            </Button>
          ) : (
            <Card className="cgc-card border-amber-200">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold text-amber-700">Апелляция на доп. время</h3>
                <p className="text-xs text-muted-foreground">Укажите причину пересмотра. Это последняя попытка.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Часы</label>
                    <select value={reqHours} onChange={e => setReqHours(parseInt(e.target.value))}
                      className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer mt-1">
                      {[0,1,2,3,4,5,6,7,8,9,10].map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Минуты</label>
                    <select value={reqMinutes} onChange={e => setReqMinutes(parseInt(e.target.value))}
                      className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer mt-1">
                      {[0,15,30,45].map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                    </select>
                  </div>
                </div>
                <textarea value={reqReason} onChange={e => setReqReason(e.target.value)}
                  placeholder="Почему вы не согласны с решением? (мин. 10 символов)"
                  className="w-full min-h-[60px] p-2 text-sm rounded-lg border bg-background resize-none" />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowTimeRequest(false)}>Отмена</Button>
                  <Button className="flex-1 bg-amber-600 hover:bg-amber-700" onClick={onSubmit} disabled={reqLoading}>
                    {reqLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Отправить апелляцию
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* First request: no requests yet */}
      {canSubmitFirst && (
        <>
          {!showTimeRequest ? (
            <Button variant="outline" className="w-full" onClick={() => setShowTimeRequest(true)}>
              <Clock className="mr-2 h-4 w-4" /> Запросить доп. время
            </Button>
          ) : (
            <Card className="cgc-card">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold">Запрос доп. времени</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Часы</label>
                    <select value={reqHours} onChange={e => setReqHours(parseInt(e.target.value))}
                      className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer mt-1">
                      {[0,1,2,3,4,5,6,7,8,9,10].map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Минуты</label>
                    <select value={reqMinutes} onChange={e => setReqMinutes(parseInt(e.target.value))}
                      className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer mt-1">
                      {[0,15,30,45].map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                    </select>
                  </div>
                </div>
                <textarea value={reqReason} onChange={e => setReqReason(e.target.value)}
                  placeholder="Причина запроса (мин. 10 символов)..."
                  className="w-full min-h-[60px] p-2 text-sm rounded-lg border bg-background resize-none" />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowTimeRequest(false)}>Отмена</Button>
                  <Button className="flex-1" onClick={onSubmit} disabled={reqLoading}>
                    {reqLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Отправить
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ─── TAB 2: МОИ ЗАКАЗЫ ───────────────────────────────────────────

function MyJobsTab({ orders, onOrderAction, actionLoading, fetchData }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleActionWrapped = async (action, orderId, noteText) => {
    await onOrderAction(action, orderId, noteText);
    if (action === 'finish-work') setDetailOpen(false);
  };

  const activeOrders = orders.filter(o => o.operational_status === 'in_progress');
  const pendingOrders = orders.filter(o => ['new', 'confirmed'].includes(o.operational_status));
  const doneOrders = orders.filter(o => o.operational_status === 'done');

  return (
    <div className="space-y-4">
      {activeOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-amber-600 flex items-center gap-1.5">
            <Play className="h-4 w-4" /> В работе ({activeOrders.length})
          </h3>
          {activeOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} isActive />
          ))}
        </div>
      )}

      {pendingOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Calendar className="h-4 w-4" /> Предстоящие ({pendingOrders.length})
          </h3>
          {pendingOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} />
          ))}
        </div>
      )}

      {doneOrders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-green-600 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Выполнено ({doneOrders.length})
          </h3>
          {doneOrders.map(order => (
            <WorkerOrderCard key={order.id} order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }} />
          ))}
        </div>
      )}

      {orders.length === 0 && (
        <Card className="cgc-card">
          <CardContent className="p-8 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">Нет заказов на сегодня</p>
          </CardContent>
        </Card>
      )}

      <OrderDetailSheet
        order={selectedOrder}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onAction={handleActionWrapped}
        actionLoading={actionLoading}
        allOrders={orders}
      />
    </div>
  );
}


// ─── TAB 3: МОИ ЧАСЫ ─────────────────────────────────────────────

function MyHoursTab({ api }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [disputeForm, setDisputeForm] = useState(null);
  const [disputeMinutes, setDisputeMinutes] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [adjExpanded, setAdjExpanded] = useState(false);

  const fetchHours = useCallback(async () => {
    try {
      const to = localToday();
      const from = localDaysAgo(30);
      const res = await api.get(`/worker/my-hours?from_date=${from}&to_date=${to}`);
      setSubmissions(res.data || []);
    } catch (err) {
      console.error('Failed to load hours:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchHours(); }, [fetchHours]);

  // Fetch adjustment request history
  useEffect(() => {
    api.get('/time-adjustments').then(res => {
      setAdjustments(res.data || []);
    }).catch(() => {});
  }, [api]);

  const handleDispute = async () => {
    if (!disputeForm) return;
    const mins = parseInt(disputeMinutes);
    if (!mins || mins <= 0) { toast.error('Укажите время'); return; }
    if (disputeReason.trim().length < 5) { toast.error('Опишите причину'); return; }

    setDisputeLoading(true);
    try {
      await api.post('/worker/dispute-block', {
        submission_id: disputeForm.submissionId,
        block_index: disputeForm.blockIndex,
        block_type: disputeForm.block.type,
        system_minutes: disputeForm.block.duration_minutes,
        worker_minutes: mins,
        reason: disputeReason.trim(),
      });
      toast.success('Запрос на пересмотр отправлен');
      setDisputeForm(null);
      setDisputeMinutes('');
      setDisputeReason('');
      fetchHours();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setDisputeLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-3">

      {/* Adjustment requests history */}
      {adjustments.length > 0 && (
        <Card className="cgc-card">
          <CardContent className="p-0">
            <button
              className="w-full p-3 flex items-center justify-between text-left"
              onClick={() => setAdjExpanded(!adjExpanded)}
            >
              <span className="text-sm font-semibold flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-amber-500" />
                Запросы на доп. время
                {adjustments.filter(a => a.status === 'pending').length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                    {adjustments.filter(a => a.status === 'pending').length} ожидает
                  </span>
                )}
              </span>
              <ChevronRight className={cn("h-4 w-4 transition-transform text-muted-foreground", adjExpanded && "rotate-90")} />
            </button>
            {adjExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t">
                {adjustments.map(adj => (
                  <div key={adj.id} className={cn(
                    "p-3 rounded-lg border text-sm",
                    adj.status === 'pending' && "bg-amber-50 dark:bg-amber-900/10 border-amber-200",
                    adj.status === 'approved' && "bg-green-50 dark:bg-green-900/10 border-green-200",
                    adj.status === 'rejected' && "bg-red-50 dark:bg-red-900/10 border-red-200",
                  )}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{adj.date}</span>
                      <Badge variant="outline" className={cn("text-[10px]",
                        adj.status === 'pending' && "border-amber-400 text-amber-700",
                        adj.status === 'approved' && "border-green-500 text-green-700",
                        adj.status === 'rejected' && "border-red-400 text-red-700",
                      )}>
                        {adj.status === 'pending' ? 'На рассмотрении' : adj.status === 'approved' ? '✓ Одобрено' : '✕ Отклонено'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Запрошено: +{adj.requested_hours}ч {adj.requested_minutes > 0 ? `${adj.requested_minutes}мин` : ''}
                    </p>
                    {adj.status === 'approved' && (
                      <p className="text-xs text-green-700 font-medium mt-0.5">
                        Одобрено: +{adj.approved_hours}ч {adj.approved_minutes > 0 ? `${adj.approved_minutes}мин` : ''}
                      </p>
                    )}
                    {adj.manager_comment && (
                      <p className="text-xs mt-1 italic">«{adj.manager_comment}»</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1 truncate">Причина: {adj.reason}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <h3 className="text-sm font-semibold flex items-center gap-1.5">
        <Clock className="h-4 w-4" /> История часов (30 дней)
      </h3>

      {submissions.length === 0 && (
        <Card className="cgc-card">
          <CardContent className="p-6 text-center text-muted-foreground">
            Нет данных за период
          </CardContent>
        </Card>
      )}

      {submissions.map(sub => {
        const isExpanded = expandedId === sub.id;
        const statusColor = sub.status === 'approved' ? 'text-green-600' :
          sub.status === 'rejected' ? 'text-red-600' :
          sub.status === 'disputed' ? 'text-amber-600' : 'text-gray-500';
        const statusLabel = sub.status === 'approved' ? 'Одобрено' :
          sub.status === 'rejected' ? 'Отклонено' :
          sub.status === 'disputed' ? 'Оспорено' : 'На проверке';

        return (
          <Card key={sub.id} className="cgc-card">
            <CardContent className="p-0">
              <button
                className="w-full p-3 flex items-center justify-between text-left"
                onClick={() => setExpandedId(isExpanded ? null : sub.id)}
              >
                <div>
                  <p className="text-sm font-semibold">{sub.date}</p>
                  <p className="text-xs text-muted-foreground">
                    {sub.total_hours != null ? `${sub.total_hours}ч` : '—'} | {sub.orders_completed || 0} заказов
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn("text-xs font-medium", statusColor)}>{statusLabel}</span>
                  <ChevronRight className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
                </div>
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-2 border-t">
                  {/* Fair hours summary */}
                  {sub.total_paid_minutes != null && (
                    <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                      <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/10">
                        <p className="text-[10px] text-muted-foreground">Работа</p>
                        <p className="text-sm font-semibold text-green-600">{formatMinutes(sub.total_work_minutes)}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/10">
                        <p className="text-[10px] text-muted-foreground">Дорога</p>
                        <p className="text-sm font-semibold text-blue-600">{formatMinutes(sub.total_travel_minutes)}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/10">
                        <p className="text-[10px] text-muted-foreground">Перерыв</p>
                        <p className="text-sm font-semibold text-gray-500">{formatMinutes(sub.total_break_minutes)}</p>
                      </div>
                    </div>
                  )}

                  {/* Block-by-block detail */}
                  {sub.fair_hours_breakdown && sub.fair_hours_breakdown.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {sub.fair_hours_breakdown.map((block, idx) => {
                        const blockDispute = (sub.disputes || []).find(d => d.block_index === idx);
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/30">
                            <div className="flex items-center gap-1.5">
                              {block.type === 'work' && <Briefcase className="h-3 w-3 text-green-600" />}
                              {block.type === 'travel' && <Car className="h-3 w-3 text-blue-600" />}
                              {block.type === 'break' && <Coffee className="h-3 w-3 text-gray-500" />}
                              <span className="truncate max-w-[140px]">
                                {block.type === 'work' ? (block.order_title || 'Работа') :
                                 block.type === 'travel' ? 'Дорога' : 'Перерыв'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">{formatMinutes(block.duration_minutes)}</span>
                              <span className={block.is_paid ? 'text-green-600' : 'text-gray-400'}>
                                {block.is_paid ? '$' : '—'}
                              </span>
                              {/* Dispute button — only if no prior dispute for this block */}
                              {block.type !== 'break' && sub.status !== 'approved' && !blockDispute && (
                                <button
                                  className="text-amber-600 hover:text-amber-700 underline text-[10px]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDisputeForm({ submissionId: sub.id, blockIndex: idx, block });
                                  }}
                                >
                                  оспорить
                                </button>
                              )}
                              {/* Already disputed — show status, no retry */}
                              {block.type !== 'break' && blockDispute && (
                                <span className={cn("text-[10px]",
                                  blockDispute.status === 'approved' && 'text-green-600',
                                  blockDispute.status === 'rejected' && 'text-red-400',
                                  blockDispute.status === 'pending' && 'text-amber-600',
                                )}>
                                  {blockDispute.status === 'approved' ? '✓ принято' :
                                   blockDispute.status === 'rejected' ? 'отклонено' : 'на рассмотрении'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Existing disputes */}
                  {sub.disputes && sub.disputes.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] font-semibold text-amber-600">Оспоренные блоки:</p>
                      {sub.disputes.map((d, i) => (
                        <div key={i} className="text-[11px] p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200/50">
                          <p>Блок #{d.block_index + 1}: {d.system_minutes} мин → {d.worker_minutes} мин</p>
                          <p className="text-muted-foreground">Причина: {d.reason}</p>
                          <Badge variant="outline" className={cn("text-[10px] mt-1",
                            d.status === 'approved' && 'border-green-500 text-green-600',
                            d.status === 'rejected' && 'border-red-500 text-red-600',
                            d.status === 'pending' && 'border-amber-500 text-amber-600',
                          )}>
                            {d.status === 'approved' ? 'Одобрено' : d.status === 'rejected' ? 'Отклонено' : 'На рассмотрении'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Dispute form sheet */}
      <Sheet open={!!disputeForm} onOpenChange={(open) => { if (!open) setDisputeForm(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>Оспорить блок</SheetTitle>
            <SheetDescription>
              {disputeForm && (
                <span>
                  {disputeForm.block.type === 'work' ? 'Работа' : 'Дорога'}: система насчитала {formatMinutes(disputeForm.block.duration_minutes)}
                </span>
              )}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-sm font-medium">Сколько минут должно быть?</label>
              <input
                type="number"
                value={disputeMinutes}
                onChange={e => setDisputeMinutes(e.target.value)}
                placeholder="Минуты"
                className="w-full mt-1 p-2 rounded-lg border bg-background text-sm"
                min={1}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Причина</label>
              <textarea
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
                placeholder="Опишите почему время должно быть другим..."
                className="w-full mt-1 p-2 rounded-lg border bg-background text-sm min-h-[80px] resize-none"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDisputeForm(null)}>Отмена</Button>
              <Button className="flex-1" onClick={handleDispute} disabled={disputeLoading}>
                {disputeLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Отправить
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}


// ─── TAB 4: МОИ ВЫПЛАТЫ ──────────────────────────────────────────

function MyEarningsTab({ api }) {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const to = localToday();
    const from = localDaysAgo(30);
    api.get(`/worker/my-earnings?from_date=${from}&to_date=${to}`).then(res => {
      setEarnings(res.data);
    }).catch(err => {
      console.error('Failed to load earnings:', err);
    }).finally(() => {
      setLoading(false);
    });
  }, [api]);

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  }

  if (!earnings) {
    return (
      <Card className="cgc-card">
        <CardContent className="p-6 text-center text-muted-foreground">
          Нет данных о выплатах
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <Card className="cgc-card border-accent/20">
        <CardContent className="p-4">
          <div className="text-center mb-4">
            <p className="text-sm text-muted-foreground">Заработок за 30 дней</p>
            <p className="text-3xl font-bold text-accent mt-1">
              ${(earnings.estimated_earnings || 0).toFixed(2)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/10 text-center">
              <p className="text-[10px] text-muted-foreground">Одобрено часов</p>
              <p className="text-lg font-bold text-green-600">{(earnings.approved_hours || 0).toFixed(1)}</p>
            </div>
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 text-center">
              <p className="text-[10px] text-muted-foreground">На проверке</p>
              <p className="text-lg font-bold text-amber-600">{(earnings.pending_hours || 0).toFixed(1)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rate info */}
      {earnings.hourly_rate != null && (
        <Card className="cgc-card">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-accent" />
              <span className="text-sm">Ваша ставка</span>
            </div>
            <span className="text-sm font-bold">${earnings.hourly_rate}/ч</span>
          </CardContent>
        </Card>
      )}

      {/* Days breakdown */}
      {earnings.days && earnings.days.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <FileText className="h-4 w-4" /> По дням
          </h3>
          {earnings.days.map((day, i) => (
            <Card key={i} className="cgc-card">
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{day.date}</p>
                  <p className="text-xs text-muted-foreground">
                    {day.hours?.toFixed(1) || 0}ч | {day.orders_completed || 0} заказов
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">${(day.earned || 0).toFixed(2)}</p>
                  <Badge variant="outline" className={cn("text-[10px]",
                    day.status === 'approved' && 'border-green-500 text-green-600',
                    day.status === 'rejected' && 'border-red-500 text-red-600',
                    'border-gray-300 text-gray-500',
                  )}>
                    {day.status === 'approved' ? 'Одобрено' : day.status === 'rejected' ? 'Отклонено' : 'Ожидает'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Shared Components ────────────────────────────────────────────

function WorkerOrderCard({ order, onOpen, isActive }) {
  return (
    <button
      onClick={() => onOpen(order)}
      className={cn(
        "w-full text-left p-4 rounded-xl border transition-all hover:shadow-md",
        isActive && "border-amber-400 bg-amber-50 dark:bg-amber-900/10 ring-2 ring-amber-400/30",
        !isActive && "border-border hover:border-accent/30"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: (order.service_color || '#6366f1') + '20' }}
          >
            <Briefcase className="h-5 w-5" style={{ color: order.service_color || '#6366f1' }} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{order.title}</p>
            <p className="text-xs text-muted-foreground">{order.client_name}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {order.scheduled_time_start && (
            <Badge variant="outline" className="text-xs font-mono">
              {order.scheduled_time_start}
            </Badge>
          )}
          <Badge className={cn("text-[10px]", STATUS_CONFIG[order.operational_status]?.color)}>
            {STATUS_CONFIG[order.operational_status]?.label}
          </Badge>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        {order.client_address && (
          <span className="flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{order.client_address}</span>
          </span>
        )}
        {order.estimated_duration_minutes && (
          <span className="flex items-center gap-1 shrink-0">
            <Clock className="h-3 w-3" /> {order.estimated_duration_minutes} мин
          </span>
        )}
      </div>
      {order.service_required && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Требуется доп. обслуживание</span>
        </div>
      )}
    </button>
  );
}

function OrderDetailSheet({ order, open, onOpenChange, onAction, actionLoading, allOrders }) {
  const [note, setNote] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [geoError, setGeoError] = useState('');
  const [geoChecking, setGeoChecking] = useState(false);

  const handleStartWork = () => {
    if (!navigator.geolocation) {
      setGeoError('Геолокация не поддерживается');
      return;
    }
    setGeoError('');
    setGeoChecking(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoChecking(false);
        onAction('start-work', order.id);
      },
      () => {
        setGeoChecking(false);
        setGeoError('Разрешите доступ к геолокации чтобы начать работу.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (!order || order.operational_status !== 'in_progress') {
      setCountdown(null);
      setElapsed(0);
      return;
    }
    const estimatedSec = (order.estimated_duration_minutes || 0) * 60;
    const startedAt = order.started_at ? new Date(order.started_at) : new Date();
    const tick = () => {
      const now = new Date();
      const elapsedSec = Math.floor((now - startedAt) / 1000);
      setElapsed(elapsedSec);
      setCountdown(estimatedSec - elapsedSec);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [order?.id, order?.operational_status, order?.started_at, order?.estimated_duration_minutes]);

  if (!order) return null;

  const handleCall = () => { if (order.client_phone) window.location.href = `tel:${order.client_phone}`; };
  const handleSms = () => { if (order.client_phone) window.location.href = `sms:${order.client_phone}`; };
  const handleRoute = () => {
    const addr = encodeURIComponent(order.client_address || order.title);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${addr}`, '_blank');
  };

  const isOvertime = countdown !== null && countdown < 0;
  const isAlmostDone = countdown !== null && countdown >= 0 && countdown < 300;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-2xl">
        <SheetHeader className="text-left">
          <SheetTitle className="truncate text-lg">{order.title}</SheetTitle>
          <SheetDescription asChild>
            <div className="flex items-center gap-2">
              <Badge className={cn("text-xs", STATUS_CONFIG[order.operational_status]?.color)}>
                {STATUS_CONFIG[order.operational_status]?.label}
              </Badge>
              {order.service_name && <span className="text-xs">{order.service_name}</span>}
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4 overflow-y-auto pb-safe" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" className="h-16 flex-col gap-1" onClick={handleCall} disabled={!order.client_phone}>
              <Phone className="h-5 w-5 text-green-500" />
              <span className="text-xs">Звонок</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col gap-1" onClick={handleSms} disabled={!order.client_phone}>
              <MessageSquare className="h-5 w-5 text-blue-500" />
              <span className="text-xs">SMS</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col gap-1" onClick={handleRoute}>
              <Navigation className="h-5 w-5 text-accent" />
              <span className="text-xs">Маршрут</span>
            </Button>
          </div>

          {/* Client info */}
          <Card className="border">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">{order.client_address || 'Адрес не указан'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">
                  {order.scheduled_time_start || '?'} — {order.scheduled_time_end || '?'} ({formatDuration(order.estimated_duration_minutes)})
                </span>
              </div>
              {order.description && (
                <p className="text-sm text-muted-foreground pt-1 border-t">{order.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Action / Timer */}
          <div className="space-y-2">
            {(order.operational_status === 'new' || order.operational_status === 'confirmed') && (
              <>
                <Button
                  className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
                  onClick={handleStartWork}
                  disabled={actionLoading || geoChecking}
                >
                  {(actionLoading || geoChecking) ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />}
                  {geoChecking ? 'Проверка геолокации...' : 'Начать работу'}
                </Button>
                {geoError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{geoError}</span>
                  </div>
                )}
              </>
            )}
            {order.operational_status === 'in_progress' && (
              <>
                <div className={cn(
                  "p-4 rounded-xl border-2 text-center transition-colors",
                  isOvertime ? "border-red-500/50 bg-red-500/10" :
                  isAlmostDone ? "border-amber-500/50 bg-amber-500/10" :
                  "border-green-500/50 bg-green-500/10"
                )}>
                  <p className="text-xs text-muted-foreground mb-1">
                    {isOvertime ? 'Превышение времени' : 'Осталось'}
                  </p>
                  <p className={cn(
                    "text-4xl font-mono font-bold tabular-nums",
                    isOvertime ? "text-red-600" : isAlmostDone ? "text-amber-600" : "text-green-600"
                  )}>
                    {countdown !== null ? formatTime(countdown) : '--:--'}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">из {formatDuration(order.estimated_duration_minutes)}</p>
                  <div className="w-full h-1.5 rounded-full bg-muted mt-3 overflow-hidden">
                    <div
                      className={cn("h-full rounded-full transition-all duration-1000",
                        isOvertime ? "bg-red-500" : isAlmostDone ? "bg-amber-500" : "bg-green-500"
                      )}
                      style={{ width: `${Math.min(100, (elapsed / ((order.estimated_duration_minutes || 1) * 60)) * 100)}%` }}
                    />
                  </div>
                </div>
                <Button
                  className={cn("w-full h-14 text-base", isOvertime ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700")}
                  onClick={() => onAction('finish-work', order.id)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle2 className="mr-2 h-5 w-5" />}
                  Завершить работу
                </Button>
              </>
            )}
            {order.operational_status !== 'done' && order.operational_status !== 'cancelled' && (
              <Button
                variant="outline"
                className="w-full h-12 text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => onAction('mark-requires-service', order.id)}
                disabled={actionLoading || order.service_required}
              >
                <Wrench className="mr-2 h-4 w-4" />
                {order.service_required ? 'Уже отмечено' : 'Треб. доп. обслуживание'}
              </Button>
            )}
          </div>

          {/* Note */}
          {order.operational_status !== 'done' && order.operational_status !== 'cancelled' && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Добавить заметку</p>
              <div className="flex gap-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Комментарий к заказу..." className="min-h-[44px] resize-none" rows={2} />
                <Button size="icon" className="shrink-0 h-[68px] w-12"
                  disabled={!note.trim() || actionLoading}
                  onClick={() => { onAction('add-note', order.id, note.trim()); setNote(''); }}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Photos */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Фото</p>
            <OrderPhotosGallery orderId={order.id} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ─── MAIN: WorkerCabinet ──────────────────────────────────────────

export default function WorkerCabinet({ user }) {
  const { api } = useAuth();
  const [dayStatus, setDayStatus] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const alertedOrderIds = useRef(new Set());

  const fetchData = useCallback(async () => {
    try {
      const today = localToday();
      const [statusRes, ordersRes] = await Promise.all([
        api.get(`/worker/me/day-status?date=${today}`),
        api.get(`/worker/me/orders?date=${today}`),
      ]);
      setDayStatus(statusRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error('Failed to fetch worker data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Geo-tracker
  useEffect(() => {
    if (!navigator.geolocation) return;
    const trackableOrders = orders.filter(o =>
      o.operational_status === 'in_progress' && o.service_lat && o.service_lng
    );
    if (trackableOrders.length === 0) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        for (const order of trackableOrders) {
          if (alertedOrderIds.current.has(order.id)) continue;
          const dist = haversineMeters(latitude, longitude, order.service_lat, order.service_lng);
          if (dist > 300) {
            alertedOrderIds.current.add(order.id);
            const isLastOrder = orders.filter(
              o => !['done', 'cancelled'].includes(o.operational_status)
            ).length === 1;
            api.post(`/orders/${order.id}/location-alert`, {
              worker_lat: latitude,
              worker_lng: longitude,
              is_last_order: isLastOrder,
            }).catch(() => {});
          }
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [orders, api]);

  const handleDayAction = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/worker/me/day/${action}`);
      toast.success(action === 'start' ? 'Рабочий день начат!' : 'Рабочий день завершён!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOrderAction = async (action, orderId, noteText) => {
    setActionLoading(true);
    try {
      if (action === 'add-note') {
        await api.post(`/orders/${orderId}/notes`, { text: noteText });
        toast.success('Заметка добавлена');
      } else {
        const res = await api.post(`/orders/${orderId}/actions/${action}`);
        if (action === 'start-work') {
          const data = res.data;
          if (data.gps_status === 'verified') {
            toast.success('Работа начата! GPS подтверждён');
          } else if (data.gps_status === 'warning') {
            toast.success('Работа начата. GPS: ~' + Math.round(data.gps_distance_meters || 0) + 'м от объекта');
          } else if (data.gps_status === 'flagged') {
            toast.warning('Работа начата, но GPS показывает вас далеко от объекта');
          } else {
            toast.success('Работа начата!');
          }
        } else if (action === 'finish-work') {
          const data = res.data;
          if (data.speed_reward) {
            toast.success(data.speed_reward.message, { duration: 5000 });
          } else {
            toast.success('Работа завершена!');
          }
        } else {
          toast.success('Выполнено');
        }
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-20">
      {/* Greeting */}
      <div className="mb-4">
        <h1 className="text-xl md:text-2xl font-bold text-foreground">
          {user.first_name ? `Привет, ${user.first_name}!` : 'Кабинет работника'}
        </h1>
      </div>

      <Tabs defaultValue="my-day" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="my-day" className="text-xs px-1">
            <Calendar className="h-3.5 w-3.5 mr-1 hidden sm:inline" />
            Мой день
          </TabsTrigger>
          <TabsTrigger value="my-jobs" className="text-xs px-1">
            <Briefcase className="h-3.5 w-3.5 mr-1 hidden sm:inline" />
            Заказы
          </TabsTrigger>
          <TabsTrigger value="my-hours" className="text-xs px-1">
            <Clock className="h-3.5 w-3.5 mr-1 hidden sm:inline" />
            Часы
          </TabsTrigger>
          <TabsTrigger value="my-earnings" className="text-xs px-1">
            <DollarSign className="h-3.5 w-3.5 mr-1 hidden sm:inline" />
            Выплаты
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-day">
          <MyDayTab
            dayStatus={dayStatus}
            orders={orders}
            onDayAction={handleDayAction}
            onOrderAction={handleOrderAction}
            actionLoading={actionLoading}
            api={api}
            fetchData={fetchData}
          />
        </TabsContent>

        <TabsContent value="my-jobs">
          <MyJobsTab
            orders={orders}
            onOrderAction={handleOrderAction}
            actionLoading={actionLoading}
            fetchData={fetchData}
          />
        </TabsContent>

        <TabsContent value="my-hours">
          <MyHoursTab api={api} />
        </TabsContent>

        <TabsContent value="my-earnings">
          <MyEarningsTab api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
